import { describeApiError, type PointsSystem, type PointsSystemVersion } from '@brush/api-client';
import {
  ErrorNotice,
  ErrorRetry,
  PlatformBadge,
  PointsSystemBuilder,
  Spinner,
  VersionStatusPill,
  blankScope,
  breakdownState,
  compareState,
  comparisonBaseline,
  definitionKey,
  readDefinition,
  readScope,
  scopeEventsFrom,
  scopeKey,
  tagVocabularyFromUsage,
  toDefinitionDocument,
  toPreviewState,
  toScopeDocument,
  unsavedWorkLabel,
  useDebouncedValue,
  validateDefinition,
  validateScope,
  type PointsBreakdownAccess,
  type PointsCompare,
  type PointsDefinition,
  type PointsScope,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreatePointsSystemVersion,
  useDeletePointsSystemVersion,
  usePatchPointsSystem,
  usePlatformTags,
  usePointsPreview,
  usePointsPreviewBreakdown,
  usePointsSystemVersion,
  usePublishPointsSystemVersion,
  useSavePointsSystemVersion,
  useScopeCandidates,
  useTournamentTagUsage,
} from '../../queries';
import { useRoom } from '../../room';
import { useUnsavedWork } from '../../unsaved';
import { Modal, btnGhost, btnPrimary, modalControl } from '../Modal';

/**
 * The designer, for one of this room's systems — or a read-only look at one of the platform's.
 *
 * The preview needs no room selector here: a room scores its own events and nothing else, which
 * the server enforces in the scope resolver rather than trusting the client. So the board on the
 * right is always this room's real results, run through the definition on the left.
 *
 * The draft, the save and §9.6's publish live in this file; everything visible is the shared
 * builder, so this screen and the platform console's cannot drift apart.
 */
export function PointsSystemBuilderScreen({
  system,
  onBack,
  onDiscarded,
  onCloneRequest,
}: {
  system: PointsSystem;
  /** Routed through the unsaved-work guard by the caller, like every other way out of an editor. */
  onBack: () => void;
  /**
   * Leaving after the draft has been deleted. Separate from `onBack` on purpose: it must *not*
   * go through the unsaved-work guard, since asking "leave without saving?" about a draft the
   * operator has just deliberately thrown away is nonsense.
   */
  onDiscarded: () => void;
  /** Offered instead of editing on a platform system. See §2.2. */
  onCloneRequest?: () => void;
}) {
  const versions = [...system.versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const newest = versions[0]?.versionNumber ?? null;
  const [versionNumber, setVersionNumber] = useState<number | null>(newest);

  // Only fills in a missing selection. It deliberately does not follow the newest version: an
  // operator who chose v1 to read it would otherwise be snapped back to v3 on the next refetch.
  useEffect(() => {
    if (versionNumber === null && newest !== null) setVersionNumber(newest);
  }, [newest, versionNumber]);

  const { roomId } = useRoom();
  const version = usePointsSystemVersion(system.id, versionNumber);
  const createVersion = useCreatePointsSystemVersion(roomId);

  const known = versions.map((v) => ({ versionNumber: v.versionNumber, status: v.status }));
  // A version created a moment ago is edited before the catalogue has refetched, so the selected
  // number can legitimately be missing from the list. Without this the select renders blank,
  // which reads as having lost the new draft.
  const options =
    versionNumber !== null && !known.some((v) => v.versionNumber === versionNumber)
      ? [{ versionNumber, status: 'draft' }, ...known]
      : known;
  const status = options.find((v) => v.versionNumber === versionNumber)?.status ?? 'draft';

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55 transition hover:text-ink-950"
      >
        ← All points systems
      </button>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        {/* An `h2`: the page is Settings, and this is a screen within one of its panes. */}
        <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-semibold text-ink-950">
          {system.isPlatform ? <PlatformBadge /> : null}
          {system.name}
          {/* §9.6's draft badge, beside the name: it answers "can I change this", which is the
              first thing anybody opening this screen wants to know. */}
          {versionNumber !== null ? <VersionStatusPill status={status} /> : null}
        </h2>

        {options.length > 0 && versionNumber !== null ? (
          <div className="flex items-center gap-2">
            <label className="text-[11px] uppercase tracking-[0.08em] text-ink-800/45" htmlFor="version">
              Version
            </label>
            <select
              id="version"
              value={versionNumber}
              onChange={(e) => setVersionNumber(Number(e.target.value))}
              className={modalControl}
            >
              {options.map((v) => (
                <option key={v.versionNumber} value={v.versionNumber}>
                  v{v.versionNumber} — {v.status}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {versionNumber === null ? (
        <EmptySystem
          system={system}
          pending={createVersion.isPending}
          error={describeApiError(createVersion.error)}
          onCreate={
            system.isPlatform
              ? null
              : () =>
                  createVersion.mutate(
                    { id: system.id, copyFromVersion: null },
                    { onSuccess: (created) => setVersionNumber(created.versionNumber) },
                  )
          }
        />
      ) : version.isLoading ? (
        <Spinner label="Loading the definition…" />
      ) : version.isError || !version.data ? (
        <ErrorRetry message="Couldn't load that version." onRetry={() => void version.refetch()} />
      ) : (
        <Editor
          // Remounted per version, so switching between a published version and a draft never
          // carries one's edits into the other.
          key={`${system.id}:${versionNumber}`}
          system={system}
          version={version.data}
          onVersionCreated={setVersionNumber}
          onVersionDiscarded={onDiscarded}
          onCloneRequest={onCloneRequest}
        />
      )}
    </div>
  );
}

/** A catalogue entry with nothing in it: no picker offers it, because there is nothing to pin. */
function EmptySystem({
  system,
  onCreate,
  pending,
  error,
}: {
  system: PointsSystem;
  onCreate: (() => void) | null;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div className="border border-line bg-surface p-10 text-center">
      <p className="text-sm font-medium text-ink-950">{system.name} has no versions yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-ink-800/60">
        A system is a catalogue entry; what actually scores is a version of it. Nothing can pin
        this until it has one.
      </p>
      {onCreate ? (
        /* Not `btnPrimary`: that carries `flex-1` for a dialog footer, and a second width
           utility here would resolve by stylesheet order rather than class order. */
        <button
          type="button"
          onClick={onCreate}
          disabled={pending}
          className="mt-4 bg-felt-700 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-felt-900 disabled:opacity-50"
        >
          {pending ? 'Creating…' : 'Start version 1'}
        </button>
      ) : null}
      <ErrorNotice message={error} className="mt-4 text-left" />
    </div>
  );
}

function Editor({
  system,
  version,
  onVersionCreated,
  onVersionDiscarded,
  onCloneRequest,
}: {
  system: PointsSystem;
  version: PointsSystemVersion;
  onVersionCreated: (n: number) => void;
  /**
   * Called once a draft has been discarded. It leaves the screen rather than picking another
   * version: the catalogue has not refetched yet, so the newest version this screen knows about
   * is the one just deleted, and selecting it would show a definition that no longer exists as
   * though it were still editable.
   */
  onVersionDiscarded: () => void;
  onCloneRequest?: () => void;
}) {
  const { room, roomId } = useRoom();

  // Two separate reasons this can be read-only, and they are told apart on screen: a platform
  // system belongs to somebody else (§2.2), a published version belongs to the past (§2.3).
  const editable = !system.isPlatform && version.status === 'draft';

  // The test scope answers to only the first of those. §9.6's matrix locks `Definition` when a
  // version publishes and leaves `PreviewScope` editable at every status — it is a column of its
  // own that the engine never receives, so changing it cannot reach a leaderboard or the frozen
  // document. What still closes it is ownership: a room may not write to a platform system at all,
  // and an editable control with nowhere to save to loses the operator's work.
  const scopeEditable = !system.isPlatform;

  const [draft, setDraft] = useState<PointsDefinition>(() => readDefinition(version.definition));

  // §9.8's test scope, held beside the draft rather than inside it — it is a separate document
  // with a separate owner (§2.1, §3.2), and the server keeps it in a separate column. A version
  // that has never been tested against anything opens on the default window.
  const [scope, setScope] = useState<PointsScope>(
    () => readScope(version.previewScope) ?? blankScope(),
  );

  // What "unchanged" means: whatever was last stored. Updated on a successful save, so the footer
  // goes quiet without anything having to remember that a save happened.
  const [baseline, setBaseline] = useState(() => definitionKey(draft));
  const [scopeBaseline, setScopeBaseline] = useState(() => scopeKey(scope));
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // The test scope counts as unsaved work: it is stored on the version, so leaving without saving
  // really does lose it. But it is tracked *separately* from the definition, and the footer says
  // which one moved — a screen that answers a widened date range with "Unsaved changes" says the
  // opposite of what the preview pane says about testing not touching the system. It is also
  // emphatically not part of what publishing freezes, which is why Publish is gated on the
  // definition alone — see the publish dialog — and why this one is gated on `scopeEditable`
  // rather than on `editable`: a published version's scope is still savable.
  const definitionDirty = editable && definitionKey(draft) !== baseline;
  const scopeDirty = scopeEditable && scopeKey(scope) !== scopeBaseline;
  const dirty = definitionDirty || scopeDirty;

  // The section nav, the room switcher and the back button all route through the same guard, and
  // `beforeunload` covers closing the tab. Only the component holding the draft has to say so.
  useUnsavedWork(dirty);

  const validation = useMemo(() => validateDefinition(draft), [draft]);
  const scopeValidation = useMemo(() => validateScope(scope), [scope]);

  const save = useSavePointsSystemVersion(roomId);
  const patchSystem = usePatchPointsSystem(roomId);
  const publish = usePublishPointsSystemVersion(roomId);
  const createVersion = useCreatePointsSystemVersion(roomId);
  const deleteVersion = useDeletePointsSystemVersion(roomId);

  // The whole room's events, read page by page without results (known gap 108), and its tag vocabulary from
  // the server's counts, in place of the newest 200 tournaments with every result. The pickers reach outside
  // the test scope's dates — "always scores" is how an event outside them is admitted — so nothing narrows the
  // read. Null until it lands, which the pickers and the diagnosis say (known gap 112).
  const roomEvents = useScopeCandidates(roomId, {});
  const tagUsage = useTournamentTagUsage(roomId);
  const platformTags = usePlatformTags();

  const events = useMemo(
    () => (roomEvents.data ? scopeEventsFrom(roomEvents.data.rows) : null),
    [roomEvents.data],
  );
  const tags = useMemo(
    () => tagVocabularyFromUsage(tagUsage.data ?? [], platformTags.data ?? []),
    [tagUsage.data, platformTags.data],
  );

  // The two documents the preview takes, debounced together so a definition edit and a scope edit
  // cost one request rather than two. Long enough that dragging a spinner doesn't spend the
  // endpoint's per-minute allowance, short enough that the board still feels attached to what is
  // being typed.
  const request = useMemo(
    () => ({ definition: toDefinitionDocument(draft), scope: toScopeDocument(scope) }),
    [draft, scope],
  );
  const settled = useDebouncedValue(request, 800);

  const previewable = validation.isValid && scopeValidation.isValid;

  const preview = usePointsPreview({
    definition: settled.definition,
    scope: settled.scope,
    roomId,
    enabled: previewable,
  });

  const previewState = toPreviewState({
    blocked: !previewable,
    idleMessage: null,
    data: preview.data,
    isFetching: preview.isFetching,
    error: preview.isError ? describeApiError(preview.error) : null,
  });

  // ---- §7.4's working, for whichever row is open -------------------------------------------
  // The list carries the rank, the name and the total; everything else about one player is here,
  // behind their own row, and is fetched only when somebody asks for it.
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);

  const playerBreakdown = usePointsPreviewBreakdown({
    definition: settled.definition,
    scope: settled.scope,
    roomId: roomId,
    playerId: openPlayerId,
    enabled: previewable,
  });

  const breakdown: PointsBreakdownAccess = {
    openPlayerId,
    onOpen: setOpenPlayerId,
    state: breakdownState({
      error: playerBreakdown.isError ? describeApiError(playerBreakdown.error) : null,
      data: playerBreakdown.data,
      isFetching: playerBreakdown.isFetching,
    }),
  };

  // ---- §9.8's compare: this draft against what the room is being scored by now ---------------
  const [comparing, setComparing] = useState(false);
  const baselineVersion = comparisonBaseline(system, version.versionNumber);

  // Only fetched once compare is switched on. The board it produces costs a second call to the
  // endpoint whose per-caller allowance an operator spends by editing, so nobody who has not asked
  // the question pays for it.
  const published = usePointsSystemVersion(
    system.id,
    comparing ? (baselineVersion.version?.versionNumber ?? null) : null,
  );

  const comparePreview = usePointsPreview({
    definition: published.data?.definition ?? null,
    // The *same* debounced scope as the draft's board. Comparing two boards scored over different
    // events would report movement that is entirely the scope's doing.
    scope: settled.scope,
    roomId,
    enabled: comparing && previewable && published.data !== undefined,
  });

  const compare: PointsCompare = {
    baseline: baselineVersion.version
      ? { short: baselineVersion.short, long: baselineVersion.long }
      : null,
    reason: baselineVersion.reason ?? undefined,
    on: comparing,
    onToggle: setComparing,
    state: compareState({
      on: comparing,
      error:
        describeApiError(published.error) ??
        (comparePreview.isError ? describeApiError(comparePreview.error) : null),
      data: comparePreview.data,
      // Either board still moving means the two are not yet answering the same question.
      settling: preview.isFetching || comparePreview.isFetching || published.isLoading,
    }),
  };

  const submitSave = () => {
    save.mutate(
      {
        id: system.id,
        n: version.versionNumber,
        // A PUT carries the whole version, so the definition rides along even when the test scope
        // is the only thing that moved. On a frozen version it is sent back **exactly as it was
        // read** rather than as `toDefinitionDocument(draft)` — that is this build's shape of the
        // document, and a published one may carry keys this build knows nothing about. The server
        // compares and refuses a changed document (409), which is the right answer to a definition
        // this screen had quietly rewritten on the way through.
        definition: editable ? toDefinitionDocument(draft) : version.definition,
        previewScope: toScopeDocument(scope),
      },
      {
        onSuccess: () => {
          setBaseline(definitionKey(draft));
          setScopeBaseline(scopeKey(scope));
          // The catalogue row and the definition both carry a name, and they are written
          // together so the list never shows one thing while the document says another. Only
          // while the definition is editable: on a frozen version the name fields are read-only,
          // so any disagreement here is one that was already stored and is not this save's to fix.
          if (
            editable &&
            (draft.name.trim() !== system.name ||
              (draft.description ?? '') !== (system.description ?? ''))
          ) {
            patchSystem.mutate({
              id: system.id,
              name: draft.name.trim(),
              description: draft.description ?? '',
            });
          }
        },
      },
    );
  };

  const actionError =
    describeApiError(save.error) ??
    describeApiError(publish.error) ??
    describeApiError(createVersion.error) ??
    describeApiError(deleteVersion.error);

  const unsaved = unsavedWorkLabel({ definition: definitionDirty, scope: scopeDirty });

  return (
    <>
      <PointsSystemBuilder
        value={draft}
        onChange={setDraft}
        validation={validation}
        scope={scope}
        onScopeChange={setScope}
        scopeValidation={scopeValidation}
        readOnly={!editable}
        scopeReadOnly={!scopeEditable}
        readOnlyNotice={
          system.isPlatform ? (
            <>
              <strong>This system belongs to the platform.</strong> Every room can score with it
              as it stands, and no room can change it. Take a copy to make one that is yours.
            </>
          ) : version.status === 'published' ? (
            <>
              <strong>Version {version.versionNumber} is published, so this definition is
              frozen.</strong>{' '}
              Any leaderboard pinned to it is scored by exactly this, so it can never change. Use{' '}
              <em>Edit as new version</em> below to carry it into a fresh draft. Testing it against
              different results is still yours to do — the preview is live.
            </>
          ) : (
            <>
              <strong>Version {version.versionNumber} is archived.</strong> It is kept because a
              leaderboard may have been scored by it, and it is no longer offered to new ones. Its
              definition is frozen; you can still test it against whatever you like.
            </>
          )
        }
        tags={tags}
        events={events}
        eventsTruncated={roomEvents.data ? !roomEvents.data.complete : false}
        eventsFailed={roomEvents.isError}
        preview={previewState}
        previewHeader={
          <p className="text-[11.5px] text-ink-800/55">
            Scored over {room?.name ?? 'this room'}’s own results. A room only ever scores its own
            events.
          </p>
        }
        previewCompare={compare}
        previewBreakdown={breakdown}
        // This shell is exactly one viewport tall and `<main>` is the scrolling region, so the
        // preview sticks to the top of that region and is bounded by what is left under the
        // 3.5rem rail — not by the window.
        previewClassName="lg:sticky lg:top-0 lg:max-h-[calc(100dvh-3.5rem)]"
        footer={
          <div className="sticky bottom-0 z-10 -mx-8 mt-6 flex flex-wrap items-center gap-3 border-t border-line bg-ground/95 px-8 py-4 backdrop-blur">
            <span className="mr-auto text-xs">
              {unsaved ? (
                <>
                  {/* Orange means "you must act" in this app, and unsaved work is exactly
                      that — whichever of the two documents it is. Reachable on a frozen version
                      too, where only the test scope can have moved. */}
                  <span className="font-semibold uppercase tracking-[0.06em] text-signal">
                    {unsaved.label}
                  </span>
                  {unsaved.detail ? (
                    <span className="ml-1.5 text-ink-800/45">{unsaved.detail}</span>
                  ) : null}
                </>
              ) : editable ? (
                <span className="text-ink-800/45">All changes saved</span>
              ) : (
                // Which half is read-only, rather than "read-only" flat: the preview beside it is
                // not, and a footer saying otherwise is what stops anybody trying it.
                <span className="text-ink-800/45">
                  {scopeEditable ? 'Definition read-only' : 'Read-only'}
                </span>
              )}
            </span>

            {editable ? (
              <>
                {/* A draft hard-deletes and nothing references it (§2.4), so this needs an
                    ordinary confirmation and no more — but it does need one, since it sits
                    beside Save. */}
                {confirmDiscard ? (
                  <>
                    <span className="text-xs text-ink-800/70">Discard this draft for good?</span>
                    <button
                      type="button"
                      onClick={() => setConfirmDiscard(false)}
                      className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950"
                    >
                      Keep it
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        deleteVersion.mutate(
                          { id: system.id, n: version.versionNumber },
                          { onSuccess: onVersionDiscarded },
                        )
                      }
                      disabled={deleteVersion.isPending}
                      className="border border-chip-500/40 px-4 py-2 text-sm font-medium text-chip-500 transition hover:bg-chip-500/5 disabled:opacity-40"
                    >
                      {deleteVersion.isPending ? 'Discarding…' : 'Discard'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDiscard(true)}
                    className="border border-chip-500/40 px-4 py-2 text-sm text-chip-500 transition hover:bg-chip-500/5"
                  >
                    Discard this draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={submitSave}
                  disabled={!validation.isValid || !dirty || save.isPending}
                  className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {save.isPending ? 'Saving…' : 'Save draft'}
                </button>
                {/* Gated on the *definition*, not on the test scope. Publishing freezes the
                    document, and the test scope is not part of it (§9.8) — refusing to publish
                    over an unsaved date range would say it was. What it does cost is the scope
                    edit itself, since a published version can no longer be saved, and the dialog
                    says so rather than dropping it quietly. */}
                <button
                  type="button"
                  onClick={() => setConfirmPublish(true)}
                  disabled={!validation.isValid || definitionDirty || publish.isPending}
                  title={
                    definitionDirty
                      ? 'Save your changes first — publishing freezes the definition as stored.'
                      : undefined
                  }
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Publish version {version.versionNumber}
                </button>
              </>
            ) : system.isPlatform ? (
              onCloneRequest ? (
                <button
                  type="button"
                  onClick={onCloneRequest}
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900"
                >
                  Clone to edit
                </button>
              ) : null
            ) : (
              <>
                {/* The one write a frozen version still takes. It sends the definition back
                    untouched and the test scope as edited — §9.6's matrix, as a button. */}
                {scopeEditable ? (
                  <button
                    type="button"
                    onClick={submitSave}
                    disabled={!scopeDirty || save.isPending}
                    title="Publishing froze the definition, not what you test it against."
                    className="border border-line bg-surface px-4 py-2 text-sm text-ink-800 transition hover:border-ink-800/40 hover:text-ink-950 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {save.isPending ? 'Saving…' : 'Save test scope'}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    createVersion.mutate(
                      { id: system.id, copyFromVersion: version.versionNumber },
                      { onSuccess: (created) => onVersionCreated(created.versionNumber) },
                    )
                  }
                  disabled={createVersion.isPending}
                  className="bg-felt-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-felt-900 disabled:opacity-50"
                >
                  {createVersion.isPending ? 'Creating…' : 'Edit as new version'}
                </button>
              </>
            )}
          </div>
        }
      />

      <ErrorNotice message={actionError} className="mt-3" />

      {confirmPublish ? (
        <PublishDialog
          systemName={system.name}
          versionNumber={version.versionNumber}
          unsavedScope={scopeDirty}
          pending={publish.isPending}
          onClose={() => setConfirmPublish(false)}
          onConfirm={() =>
            publish.mutate(
              { id: system.id, n: version.versionNumber },
              { onSuccess: () => setConfirmPublish(false) },
            )
          }
        />
      ) : null}
    </>
  );
}

/** §9.6 — the confirmation says what publishing means, because it cannot be undone. */
function PublishDialog({
  systemName,
  versionNumber,
  unsavedScope,
  pending,
  onClose,
  onConfirm,
}: {
  systemName: string;
  versionNumber: number;
  /**
   * The test scope has changes that were never saved. Not a blocker, and no longer a warning
   * either — the bullet it drives says so, because "will this be lost?" is the question an
   * operator has at this moment and the answer changed.
   */
  unsavedScope: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      title={`Publish version ${versionNumber}?`}
      subtitle={systemName}
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Not yet
          </button>
          <button type="button" onClick={onConfirm} disabled={pending} className={btnPrimary}>
            {pending ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <ul className="space-y-2.5 text-sm leading-relaxed text-ink-800/80">
        <li>
          <strong className="text-ink-950">This definition freezes.</strong> A published definition
          can never be edited — not by you, not by anybody. A player asking in two years why they
          scored what they scored gets this exact document as the answer.
        </li>
        <li>
          <strong className="text-ink-950">Leaderboards already running are unaffected.</strong> A
          leaderboard pins its version when it starts and keeps it, so publishing cannot rewrite
          standings anybody has already been paid against.
        </li>
        <li>
          <strong className="text-ink-950">New leaderboards can use it.</strong> Only published
          versions appear when a leaderboard picks a system; drafts never do.
        </li>
        <li>
          To change it afterwards, use <em>Edit as new version</em> — that copies this into a fresh
          draft and leaves the published one exactly as it is.
        </li>
        {unsavedScope ? (
          <li>
            <strong className="text-ink-950">Your test scope is not part of this.</strong> It has
            unsaved changes, and they are safe: what you test against is stored beside the
            definition rather than inside it, and stays editable after publishing. Save it now or
            afterwards.
          </li>
        ) : null}
      </ul>
    </Modal>
  );
}
